"use client";

import { useEffect, useState } from "react";
import { seoApi } from '@/lib/api';
import { NeonCard } from "@/components/ui/neon-card";
import {
  ArrowUp, ArrowDown, TrendingUp, TrendingDown, Search,
  BarChart3, Loader2, ChevronDown, ChevronUp,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

interface SeoData {
  domain: string;
  date: string;
  prevDate: string | null;
  stats: {
    clicks: number; clicksChange: number;
    impressions: number; impressionsChange: number;
    avgPosition: number; positionChange: number;
    avgCtr: number; ctrChange: number;
  };
  queries: Array<{
    query: string; clicks: number; impressions: number;
    ctr: number; position: number; positionChange: number | null;
  }>;
  pages: Array<{ page: string; clicks: number; impressions: number; position: number }>;
  movers: { up: any[]; down: any[] };
  history: Array<{ date: string; total_clicks: number; total_impressions: number; avg_position: number }>;
  devices: Array<{ device: string; clicks: number; impressions: number; ctr: number; position: number }>;
  countries: Array<{ country: string; clicks: number; impressions: number; ctr: number; position: number }>;
}

function fmt1(n: number): string {
  const r = Math.round(n * 10) / 10;
  return r % 1 === 0 ? r.toString() : r.toFixed(1);
}

function StatCard({ label, value, change, suffix = "", changeLabel = "%" }: {
  label: string; value: string | number; change: number; suffix?: string; changeLabel?: string;
}) {
  return (
    <NeonCard glow="none" hover={false} className="p-4">
      <p className="text-xs text-gray-500 uppercase mb-1">{label}</p>
      <div className="flex items-end gap-2">
        <span className="text-2xl font-bold text-cyan-400">{value}{suffix}</span>
        {change !== 0 && (
          <span className={`flex items-center gap-0.5 text-sm mb-0.5 ${change > 0 ? "text-green-400" : "text-red-400"}`}>
            {change > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
            {fmt1(Math.abs(change))}{changeLabel}
          </span>
        )}
      </div>
    </NeonCard>
  );
}

function SortableTable({ queries, sortKey, sortDir, onSort }: {
  queries: SeoData["queries"]; sortKey: string; sortDir: "asc" | "desc";
  onSort: (key: string) => void;
}) {
  const SortHeader = ({ k, label }: { k: string; label: string }) => (
    <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase cursor-pointer hover:text-cyan-400 select-none" onClick={() => onSort(k)}>
      <span className="flex items-center gap-1">
        {label}
        {sortKey === k && (sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
      </span>
    </th>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/[0.04]">
            <SortHeader k="query" label="Query" />
            <SortHeader k="clicks" label="Clicks" />
            <SortHeader k="impressions" label="Impr." />
            <SortHeader k="position" label="Position" />
            <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Trend</th>
          </tr>
        </thead>
        <tbody>
          {queries.map((q, i) => (
            <tr key={i} className="border-b border-white/[0.04] hover:bg-white/5">
              <td className="px-3 py-2 text-gray-300 max-w-[200px] truncate">{q.query}</td>
              <td className="px-3 py-2 text-cyan-400 font-medium">{q.clicks}</td>
              <td className="px-3 py-2 text-gray-400">{q.impressions}</td>
              <td className="px-3 py-2 text-purple-400">{fmt1(q.position)}</td>
              <td className="px-3 py-2">
                {q.positionChange !== null && q.positionChange !== 0 ? (
                  <span className={`flex items-center gap-0.5 text-xs ${q.positionChange > 0 ? "text-green-400" : "text-red-400"}`}>
                    {q.positionChange > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                    {Math.abs(q.positionChange)}
                  </span>
                ) : <span className="text-gray-600 text-xs">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const countryFlags: Record<string, string> = {
  CAN: "🇨🇦", USA: "🇺🇸", GBR: "🇬🇧", AUS: "🇦🇺", IND: "🇮🇳", DEU: "🇩🇪", FRA: "🇫🇷",
  BRA: "🇧🇷", JPN: "🇯🇵", MEX: "🇲🇽", NLD: "🇳🇱", ESP: "🇪🇸", ITA: "🇮🇹", KOR: "🇰🇷",
};

type DateRange = 'all' | '30d' | '7d' | 'today';

function filterByRange(history: SeoData['history'], range: DateRange): SeoData['history'] {
  if (range === 'all' || history.length === 0) return history;
  // Use latest data date as reference (not today), since GSC data has a 3-day delay
  const latest = new Date(history[history.length - 1].date);
  const cutoff = new Date(latest);
  if (range === '30d') cutoff.setDate(latest.getDate() - 30);
  else if (range === '7d') cutoff.setDate(latest.getDate() - 7);
  else cutoff.setDate(latest.getDate()); // today = latest day only
  const cutoffStr = cutoff.toISOString().split('T')[0];
  return history.filter(h => h.date >= cutoffStr);
}

// Aggregate a history slice into window totals (matches the API's windowStats:
// clicks/impressions summed, position impression-weighted, ctr = clicks/impr).
function aggregate(rows: SeoData['history']) {
  const clicks = rows.reduce((s, h) => s + h.total_clicks, 0);
  const impressions = rows.reduce((s, h) => s + h.total_impressions, 0);
  const posw = rows.reduce((s, h) => s + h.avg_position * h.total_impressions, 0);
  return {
    clicks,
    impressions,
    avgCtr: impressions > 0 ? Math.round((clicks / impressions) * 1000) / 10 : 0,
    avgPosition: impressions > 0 ? Math.round((posw / impressions) * 10) / 10 : 0,
  };
}

const pct = (curr: number, prev: number) => (prev > 0 ? Math.round(((curr - prev) / prev) * 100) : 0);

// Range-aware stats: recompute the four stat cards from the selected window.
// 'today' = the API's single-latest-day stats (with day-over-day change).
// 'all' = the full history aggregate. 7d/30d aggregate the window and compare
// against the equal-length window immediately before it.
function statsForRange(data: SeoData, range: DateRange) {
  if (range === 'today') return data.stats;
  const hist = data.history || [];
  const win = range === 'all' ? hist : filterByRange(hist, range);
  if (win.length === 0) return data.stats;
  const cur = aggregate(win);
  // No prior window for all-time; else the equal-length window before this one.
  const prev = range === 'all'
    ? { clicks: 0, impressions: 0, avgCtr: 0, avgPosition: 0 }
    : aggregate(hist.slice(Math.max(0, hist.length - win.length * 2), hist.length - win.length));
  return {
    clicks: cur.clicks, clicksChange: pct(cur.clicks, prev.clicks),
    impressions: cur.impressions, impressionsChange: pct(cur.impressions, prev.impressions),
    avgPosition: cur.avgPosition,
    positionChange: prev.avgPosition > 0 ? Math.round((prev.avgPosition - cur.avgPosition) * 10) / 10 : 0,
    avgCtr: cur.avgCtr, ctrChange: pct(cur.avgCtr, prev.avgCtr),
  };
}

export function ProjectSeoTab({ domain }: { domain: string }) {
  const [data, setData] = useState<SeoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("impressions");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [range, setRange] = useState<DateRange>('all');

  useEffect(() => {
    seoApi.domain(domain)
      .then((d: any) => { if (d.error || d.noData) { setLoading(false); return; } setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [domain]);

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>;
  if (!data) return <div className="text-center py-12 text-gray-500">No SEO data for {domain}</div>;

  const sortedQueries = [...(data.queries || [])].sort((a, b) => {
    const av = (a as any)[sortKey], bv = (b as any)[sortKey];
    if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    return sortDir === "asc" ? av - bv : bv - av;
  });

  const stats = statsForRange(data, range);
  const filteredHistory = filterByRange(data.history || [], range);
  const chartData = filteredHistory.map(h => ({
    date: h.date.slice(5),
    clicks: h.total_clicks,
    impressions: h.total_impressions,
    position: Math.round(h.avg_position * 10) / 10,
  }));

  const ranges: { key: DateRange; label: string }[] = [
    { key: 'all', label: 'All Time' },
    { key: '30d', label: '30 Days' },
    { key: '7d', label: '7 Days' },
    { key: 'today', label: 'Today' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-5 h-5 text-cyan-400" />
          <h2 className="text-lg font-semibold">SEO Performance</h2>
          <span className="text-xs text-gray-500">{data.domain} · {data.date}</span>
        </div>
        <div className="flex gap-1">
          {ranges.map(r => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                range === r.key
                  ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                  : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Clicks" value={stats.clicks} change={stats.clicksChange} />
        <StatCard label="Impressions" value={stats.impressions.toLocaleString()} change={stats.impressionsChange} />
        <StatCard label="Avg Position" value={fmt1(stats.avgPosition)} change={stats.positionChange} changeLabel=" pos" />
        <StatCard label="CTR" value={fmt1(stats.avgCtr)} suffix="%" change={stats.ctrChange} />
      </div>

      {/* Charts */}
      {chartData.length > 1 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <NeonCard glow="none" hover={false} className="p-4">
            <h3 className="text-sm font-medium text-gray-400 mb-3">Clicks & Impressions</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="date" tick={{ fill: '#666', fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fill: '#666', fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fill: '#666', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: 8 }} />
                <Line yAxisId="right" type="monotone" dataKey="impressions" stroke="#60a5fa" strokeWidth={2} dot={false} />
                <Line yAxisId="left" type="monotone" dataKey="clicks" stroke="#22d3ee" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </NeonCard>
          <NeonCard glow="none" hover={false} className="p-4">
            <h3 className="text-sm font-medium text-gray-400 mb-3">Avg Position (lower is better)</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="date" tick={{ fill: '#666', fontSize: 11 }} />
                <YAxis reversed tick={{ fill: '#666', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: 8 }} />
                <Line type="monotone" dataKey="position" stroke="#a78bfa" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </NeonCard>
        </div>
      )}

      {/* Keywords Table */}
      {sortedQueries.length > 0 && (
        <NeonCard glow="none" hover={false} className="p-4">
          <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
            <Search className="w-4 h-4" />
            Top Keywords ({data.queries.length})
          </h3>
          <SortableTable queries={sortedQueries} sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
        </NeonCard>
      )}

      {/* Movers */}
      {(data.movers?.up?.length > 0 || data.movers?.down?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <NeonCard glow="green" hover={false} className="p-4">
            <h3 className="text-sm font-medium text-green-400 mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Movers Up
            </h3>
            {data.movers.up.length === 0 ? <p className="text-gray-600 text-sm">None</p> : (
              <div className="space-y-2">
                {data.movers.up.map((m: any, i: number) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-gray-300 truncate max-w-[200px]">{m.query}</span>
                    <span className="text-green-400 flex items-center gap-1">
                      {m.from} → {m.to} <ArrowUp className="w-3 h-3" />+{m.change}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </NeonCard>
          <NeonCard glow="magenta" hover={false} className="p-4">
            <h3 className="text-sm font-medium text-red-400 mb-3 flex items-center gap-2">
              <TrendingDown className="w-4 h-4" /> Movers Down
            </h3>
            {data.movers.down.length === 0 ? <p className="text-gray-600 text-sm">None</p> : (
              <div className="space-y-2">
                {data.movers.down.map((m: any, i: number) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-gray-300 truncate max-w-[200px]">{m.query}</span>
                    <span className="text-red-400 flex items-center gap-1">
                      {m.from} → {m.to} <ArrowDown className="w-3 h-3" />{m.change}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </NeonCard>
        </div>
      )}

      {/* Top Pages */}
      {data.pages?.length > 0 && (
        <NeonCard glow="none" hover={false} className="p-4">
          <h3 className="text-sm font-medium text-gray-400 mb-3">Top Pages</h3>
          <div className="space-y-2">
            {data.pages.slice(0, 10).map((p, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-gray-400 truncate max-w-[300px]">{p.page.replace(/^https?:\/\/[^/]+/, '')}</span>
                <div className="flex gap-4 text-xs">
                  <span className="text-cyan-400">{p.clicks} clicks</span>
                  <span className="text-gray-500">{p.impressions} impr</span>
                </div>
              </div>
            ))}
          </div>
        </NeonCard>
      )}

      {/* Device Breakdown */}
      {data.devices?.length > 0 && (
        <NeonCard glow="none" hover={false} className="p-4">
          <h3 className="text-sm font-medium text-gray-400 mb-3">📱 Device Breakdown</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {data.devices.map((d, i) => {
              const totalImpr = data.devices.reduce((s, x) => s + x.impressions, 0);
              const pct = totalImpr > 0 ? Math.round((d.impressions / totalImpr) * 100) : 0;
              const icon = d.device === 'MOBILE' ? '📱' : d.device === 'DESKTOP' ? '🖥️' : '📟';
              return (
                <div key={i} className="bg-white/5 border border-white/[0.08] rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span>{icon}</span>
                    <span className="text-sm font-medium text-gray-300">{d.device}</span>
                    <span className="text-xs text-gray-500 ml-auto">{pct}%</span>
                  </div>
                  <div className="w-full bg-gray-800 rounded-full h-1.5 mb-2">
                    <div className="bg-cyan-400 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{d.clicks} clicks</span>
                    <span>{d.impressions} impr</span>
                    <span>pos {fmt1(d.position)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </NeonCard>
      )}

      {/* Top Countries */}
      {data.countries?.length > 0 && (
        <NeonCard glow="none" hover={false} className="p-4">
          <h3 className="text-sm font-medium text-gray-400 mb-3">🌍 Top Countries</h3>
          <div className="space-y-2">
            {data.countries.slice(0, 5).map((c, i) => {
              const maxImpr = data.countries[0]?.impressions || 1;
              const pct = Math.round((c.impressions / maxImpr) * 100);
              return (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <span className="w-6 text-center">{countryFlags[c.country] || '🏳️'}</span>
                  <span className="w-10 text-gray-400 font-mono">{c.country}</span>
                  <div className="flex-1 bg-gray-800 rounded-full h-2">
                    <div className="bg-purple-500 h-2 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-cyan-400 text-xs w-16 text-right">{c.clicks} clicks</span>
                  <span className="text-gray-500 text-xs w-16 text-right">{c.impressions} impr</span>
                </div>
              );
            })}
          </div>
        </NeonCard>
      )}
    </div>
  );
}
